import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Link,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import PageHeader from '../components/ui/PageHeader';
import Panel from '../components/ui/Panel';
import CategoryChip from '../components/ui/CategoryChip';

const CATEGORIES = [
  { name: 'Project', blurb: 'Planned delivery work that advances a roadmap commitment.' },
  { name: 'Toil', blurb: 'Repetitive operational work that is a candidate for automation.' },
  { name: 'Unplanned', blurb: 'Reactive work: incidents, escalations and interrupts.' },
  { name: 'Change', blurb: 'Change requests and configuration work against existing systems.' },
  { name: 'Learn', blurb: 'Training, enablement, research and onboarding.' },
];

const FAQ = [
  {
    q: 'How do I get data into Squid?',
    a: 'Use the Import page to upload a JIRA CSV export. Tickets are parsed, categorised and stored. If JIRA sync is enabled on the server, tickets are also pulled automatically on a schedule.',
  },
  {
    q: 'How are tickets categorised?',
    a: 'Categorisation runs server-side at import time, using the issue type plus keyword rules against the ticket summary. Where an AI model is configured, it is used to refine the category and to estimate effort for tickets with no logged time.',
  },
  {
    q: 'What does the asterisk next to an effort value mean?',
    a: 'It marks an estimated value. Squid uses logged time where JIRA has it; where time was never logged, the effort shown is a model estimate, flagged so you never confuse it with a recorded figure.',
  },
  {
    q: 'Why do some panels show no data?',
    a: 'Panels reflect exactly what is in the imported dataset. If your JIRA export did not include a field — parent epic, for example — the panels that depend on it stay empty rather than inventing values.',
  },
  {
    q: 'How do the filters work?',
    a: 'Dashboard, Tickets and Analytics share one filter bar: time frame, project group, assignee, epic and category. Filters are applied by the API, so charts and totals always describe the same slice of data.',
  },
  {
    q: 'What time frames are available?',
    a: 'Last week, last month, quarter and last year, plus a custom range with explicit start and end dates.',
  },
];

const Help = () => (
  <Box>
    <PageHeader title="Help" subtitle="How Squid classifies work, and what the numbers mean." />

    <Grid container spacing={3}>
      <Grid size={{ xs: 12, lg: 7 }}>
        <Panel title="Getting started">
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Squid turns a JIRA export into a picture of where engineering effort actually goes —
              how much is planned delivery versus toil, unplanned interrupts, change and learning.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start on <strong>Import</strong> to load a CSV. Then use <strong>Dashboard</strong> for
              the headline split, <strong>Analytics</strong> for breakdowns by person, epic and time,
              and <strong>Tickets</strong> to inspect individual rows behind any number.
            </Typography>
          </Stack>
        </Panel>
      </Grid>

      <Grid size={{ xs: 12, lg: 5 }}>
        <Panel title="Categories">
          <Stack spacing={2}>
            {CATEGORIES.map((c) => (
              <Stack key={c.name} direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ minWidth: 96 }}>
                  <CategoryChip category={c.name} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {c.blurb}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Panel>
      </Grid>

      <Grid size={12}>
        <Panel title="Frequently asked questions">
          <Box>
            {FAQ.map((item, i) => (
              <Accordion key={item.q} disableGutters square={false} defaultExpanded={i === 0}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`faq-content-${i}`}
                  id={`faq-header-${i}`}
                >
                  <Typography variant="subtitle2">{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    {item.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Panel>
      </Grid>

      <Grid size={12}>
        <Panel title="Support">
          <Typography variant="body2" color="text.secondary">
            Found a problem or want a metric that isn&apos;t here? Raise it with the ROLLER
            engineering ops team, or open an issue on the{' '}
            <Link href="https://github.com/rollerdigital" target="_blank" rel="noopener noreferrer">
              ops-squid repository
            </Link>
            .
          </Typography>
        </Panel>
      </Grid>
    </Grid>
  </Box>
);

export default Help;
